import { signal } from "@preact/signals";
import type { HeroAvatar } from "@/content/heroes";
import type { LevelId } from "@/math";

/**
 * Several players can share one device: every hero is a profile with its own save slot in
 * localStorage. The index lists them; the current profile id says which slot `saveGame` writes.
 */

export interface ProfileMeta {
  id: string;
  name: string;
  avatar: HeroAvatar;
  level: LevelId;
  lvl: number;
  updatedAt: string;
}

export const PROFILES_KEY = "mathrpg.profiles";
export const LEGACY_SAVE_KEY = "mathrpg.save";

export const currentProfileId = signal<string | null>(null);

export function profileSaveKey(id: string): string {
  return `${LEGACY_SAVE_KEY}.${id}`;
}

export function newProfileId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function readIndex(): ProfileMeta[] {
  try {
    const raw = localStorage.getItem(PROFILES_KEY);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? (arr as ProfileMeta[]).filter((p) => p && typeof p.id === "string") : [];
  } catch {
    return [];
  }
}

function writeIndex(list: ProfileMeta[]): void {
  try {
    localStorage.setItem(PROFILES_KEY, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

/** The single-slot save from earlier versions becomes the first profile. */
function migrateLegacy(): void {
  try {
    const raw = localStorage.getItem(LEGACY_SAVE_KEY);
    if (!raw) return;
    const id = "legacy";
    localStorage.setItem(profileSaveKey(id), raw);
    const parsed = JSON.parse(raw) as {
      hero?: { name?: string; avatar?: HeroAvatar; level?: LevelId; lvl?: number };
      savedAt?: string;
    };
    upsertProfile({
      id,
      name: parsed.hero?.name ?? "Bohater",
      avatar: parsed.hero?.avatar ?? "boy",
      level: parsed.hero?.level ?? "sp6",
      lvl: parsed.hero?.lvl ?? 1,
      updatedAt: parsed.savedAt ?? new Date().toISOString(),
    });
    localStorage.removeItem(LEGACY_SAVE_KEY);
  } catch {
    /* ignore */
  }
}

/** Profiles, most recently played first. */
export function listProfiles(): ProfileMeta[] {
  migrateLegacy();
  return readIndex().sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
}

export function upsertProfile(meta: ProfileMeta): void {
  const list = readIndex().filter((p) => p.id !== meta.id);
  list.push(meta);
  writeIndex(list);
}

export function removeProfile(id: string): void {
  try {
    localStorage.removeItem(profileSaveKey(id));
  } catch {
    /* ignore */
  }
  writeIndex(readIndex().filter((p) => p.id !== id));
  if (currentProfileId.value === id) currentProfileId.value = null;
}

export function readProfileSave(id: string): string | null {
  try {
    return localStorage.getItem(profileSaveKey(id));
  } catch {
    return null;
  }
}

export function writeProfileSave(id: string, json: string, meta: ProfileMeta): void {
  try {
    localStorage.setItem(profileSaveKey(id), json);
  } catch {
    /* ignore */
  }
  upsertProfile(meta);
}
